import type { QueryClient } from "@tanstack/react-query";
import { AUTHORSHIPS_QUERY_KEY } from "@/api/authorships";
import {
  COURSE_CERTIFICATES_LIST_QUERY_KEY,
  COURSE_CERTIFICATES_QUERY_KEY,
} from "@/api/courseCertificates";
import { INSURANCE_POLICIES_QUERY_KEY } from "@/api/insurancePolicies";
import { DISPENSATIONS_QUERY_KEY } from "@/api/dispensations";
import { REDUCTIONS_QUERY_KEY } from "@/api/reductions";
import { LEGAL_AID_QUERY_KEY } from "@/api/legalAid";
import { TEACHINGS_QUERY_KEY } from "@/api/teachings";
import { AFVENTENDE_INDBERETNINGER_QUERY_KEY } from "@/hooks/useAfventendeIndberetninger";
import { REPORTING_LESSONS_STATUS_QUERY_KEY } from "./useReportingLessonsStatus";

const CLE_DOCUMENT_QUERY_KEYS = [
  COURSE_CERTIFICATES_QUERY_KEY,
  INSURANCE_POLICIES_QUERY_KEY,
  TEACHINGS_QUERY_KEY,
  REDUCTIONS_QUERY_KEY,
  LEGAL_AID_QUERY_KEY,
  AUTHORSHIPS_QUERY_KEY,
  DISPENSATIONS_QUERY_KEY,
] as const;

/** Invalidates a document family. Pass `deletedId` after a delete: the deleted
 * document's own detail/file queries are then marked stale without being
 * refetched, because the detail view holding them is still mounted when this
 * runs and the document no longer exists. */
const invalidateDocumentFamily = (
  queryClient: QueryClient,
  queryKey: readonly string[],
  deletedId?: string,
) => {
  if (!deletedId) {
    void queryClient.invalidateQueries({ queryKey });
    return;
  }

  const isDeletedDocument = (key: readonly unknown[]) =>
    key.includes(deletedId);

  // Everything except the deleted document: refetch as usual.
  void queryClient.invalidateQueries({
    queryKey,
    predicate: (query) => !isDeletedDocument(query.queryKey),
  });

  // The deleted document itself: mark stale so a later visit refetches (and
  // gets the 404 it should), but do not refetch now.
  void queryClient.invalidateQueries({
    queryKey,
    predicate: (query) => isDeletedDocument(query.queryKey),
    refetchType: "none",
  });
};

/** Invalidates the aggregate queries derived from evidence items
 * (reporting lessons status + afventende indberetninger). */
export const invalidateReportingStatusQueries = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    queryKey: REPORTING_LESSONS_STATUS_QUERY_KEY,
  });
  void queryClient.invalidateQueries({
    queryKey: AFVENTENDE_INDBERETNINGER_QUERY_KEY,
  });
};

export const invalidateCourseCertificateQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(
    queryClient,
    COURSE_CERTIFICATES_QUERY_KEY,
    deletedId,
  );
  invalidateReportingStatusQueries(queryClient);
};

/** List-only refresh, for the per-chunk reveal during a batch upload.
 *
 * The full invalidateCourseCertificateQueries also refetches the reporting
 * aggregates, which cannot have changed: freshly queued certificates
 * contribute no lessons until analysis finishes. Across ten chunks that would
 * be thirty pointless racing requests. The full version runs once when the
 * whole run ends. */
export const invalidateCourseCertificateListQueries = (
  queryClient: QueryClient,
) => {
  void queryClient.invalidateQueries({
    queryKey: COURSE_CERTIFICATES_LIST_QUERY_KEY,
  });
};

export const invalidateInsurancePolicyQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(
    queryClient,
    INSURANCE_POLICIES_QUERY_KEY,
    deletedId,
  );
  invalidateReportingStatusQueries(queryClient);
};

export const invalidateTeachingQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(queryClient, TEACHINGS_QUERY_KEY, deletedId);
  invalidateReportingStatusQueries(queryClient);
};

export const invalidateReductionQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(queryClient, REDUCTIONS_QUERY_KEY, deletedId);
  invalidateReportingStatusQueries(queryClient);
};

export const invalidateLegalAidQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(queryClient, LEGAL_AID_QUERY_KEY, deletedId);
  invalidateReportingStatusQueries(queryClient);
};

export const invalidateAuthorshipQueries = (
  queryClient: QueryClient,
  deletedId?: string,
) => {
  invalidateDocumentFamily(queryClient, AUTHORSHIPS_QUERY_KEY, deletedId);
  invalidateReportingStatusQueries(queryClient);
};

export const invalidateDispensationQueries = (queryClient: QueryClient) => {
  invalidateDocumentFamily(queryClient, DISPENSATIONS_QUERY_KEY);
  invalidateReportingStatusQueries(queryClient);
};

/** Invalidates all document families (list, detail, for-reporting, for-injunction). */
export const invalidateCleDocumentQueries = (queryClient: QueryClient) => {
  for (const queryKey of CLE_DOCUMENT_QUERY_KEYS) {
    invalidateDocumentFamily(queryClient, queryKey);
  }

  invalidateReportingStatusQueries(queryClient);
};
